import { motion } from "framer-motion";
import { ArrowRight, Phone, MapPin, Truck } from "lucide-react";
import { scrollToId } from "./nav";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-white"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/95 via-[#0A192F]/85 to-[#0A192F]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-transparent to-transparent opacity-60" />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 z-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="container-x relative z-10 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow text-[#FF6B00] mb-8"
            >
              Оптовые поставки B2B · с 2020
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-white text-3xl sm:text-5xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-semibold leading-[1.1] tracking-tight break-words"
            >
              Комплексные
              <br />
              поставки промышленной,
              <br />
              технической и{" "}
              <span className="text-[#FF6B00] relative">
                специализированной продукции
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.9, delay: 0.7 }}
                  className="hidden md:block absolute left-0 -bottom-1 h-2 w-full bg-[#FF6B00]/25 origin-left -z-10"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 text-lg lg:text-xl text-slate-200/90 max-w-2xl leading-relaxed"
            >
              B2B-поставки по всей России: комплектующие и запчасти для промышленного
              оборудования, ГСМ, хозяйственные товары, промышленная мебель и фурнитура,
              маломощные электродвигатели и электромеханические решения для предприятий.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={() => scrollToId("request")}
                className="group inline-flex items-center justify-center gap-3 bg-[#FF6B00] hover:bg-[#E66000] text-white px-8 py-5 text-base font-medium transition-all"
              >
                Получить коммерческое предложение
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-secondary-cta"
                onClick={() => scrollToId("contacts")}
                className="group inline-flex items-center justify-center gap-3 bg-transparent border border-white/40 hover:border-white text-white hover:bg-white/5 px-8 py-5 text-base font-medium transition-all"
              >
                Связаться
                <Phone className="w-4 h-4" />
              </button>
            </motion.div>
          </div>

          {/* Right side glass card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-4"
          >
            <div className="glass-card-dark p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#FF6B00] flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/70">
                  География поставок
                </div>
              </div>
              <div className="text-white font-display text-2xl lg:text-3xl font-medium leading-tight">
                Работаем во всех&nbsp;регионах России
              </div>
              <div className="mt-6 space-y-3">
                {[
                  { label: "Промышленные предприятия", value: "B2B" },
                  { label: "Автотранспорт и спецтехника", value: "ГСМ" },
                  { label: "Хозяйственные подразделения", value: "МТО" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between border-t border-white/10 pt-3"
                  >
                    <span className="text-sm text-white/80">{row.label}</span>
                    <span className="text-xs font-semibold tracking-widest text-[#FF6B00]">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2 text-white/70 text-xs">
                <MapPin className="w-4 h-4 text-[#FF6B00]" />
                Отгрузки по РФ · транспортные компании · собственная логистика партнёров
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-[#0A192F]/60 backdrop-blur-md py-4 overflow-hidden">
        <div className="flex ticker-track whitespace-nowrap gap-16 text-white/60 text-xs uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>Комплектующие</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Промышленное оборудование</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>ГСМ</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Хозяйственные товары</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Промышленная мебель</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Электродвигатели</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Складская техника</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Силовая электроника</span>
              <span className="text-[#FF6B00]">◆</span>
              <span>Доставка по РФ</span>
              <span className="text-[#FF6B00]">◆</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
